import { useState } from "react";
import { Modal } from "antd";
import db from "../../../../database";
import { SyncData } from "../Tables-Management/SyncData";
import { getWorkflowData } from "../../../../utils/getWorkflowData";
import { useEventBus } from "../../../../lib/components/EventBusProvider";
import usePOSConfigStore from "../../../../stores/usePOSConfigStore";
import useRestaurantStore from "../../../../stores/useRestaurantStore";
import { roundTo } from "../../../../utils/tillHelpers";
import { getIsInterState } from "../../../../utils/taxUtils";
import { groupItemsByTaxRate } from "../../Core/utils/taxCalculations";

/**
 * Provides handleVoid — the KOT cancellation handler with workflow approval,
 * cart recalculation, table/split sync, and DB persistence.
 */
const useVoidHandler = ({
  cart,
  setCart,
  history,
  setKeyValue,
  setShowProducts,
  tillData,
  precision,
  setCategorieFlag,
  clearCart1,
  setTableCards,
  setOcupiedTablesData,
  setReservedTablesData,
  setTableList,
  setLoading,
}) => {
  const { dispatchEvent } = useEventBus();
  const [cancelling, setCancelling] = useState(false);

  const handleVoid = async (record, cancelItemIds, lineRemarks = {}) => {
    if (cancelling) return;
    setCancelling(true);
    try {
      const { workflowRules, approvers } = await getWorkflowData();
      const cancelKotRule = workflowRules.find((res) => res.eventName === "Cancel KOT");

      if (cancelKotRule) {
        await new Promise((resolve) => {
          dispatchEvent(cancelKotRule.eventName, cancelKotRule.ruleName, cancelKotRule.ruleCondition, approvers, null, async (eventData) => {
            try {
              if (eventData?.approvalGranted) {
                await proceedWithCancellation(record, lineRemarks);
              } else {
                Modal.error({
                  title: "Approval Required",
                  content: "The cancellation request was not approved.",
                });
              }
            } finally {
              resolve();
            }
          });
        });
      } else {
        await proceedWithCancellation(record, lineRemarks);
      }
    } finally {
      setCancelling(false);
    }

    async function proceedWithCancellation(record, lineRemarks = {}) {
      let shouldClearCart = false;
      const dineInKey = useRestaurantStore.getState().dineIn;

      if (dineInKey === "Y") {
        const orders = await db.fbOrderData.where("cwrFbTableId").equals(record.cwrFbTableId).toArray();
        const ordersData = orders.find((order) => order.fbOrderStatus === "IP");
        const ordersFetched = ordersData ? [ordersData] : [];

        for (const fbOrder of ordersFetched) {
          if (fbOrder.fbOrderStatus !== "IP") continue;

          if (!fbOrder.cart && fbOrder.wsData && typeof fbOrder.wsData === "object" && Object.keys(fbOrder.wsData).length > 0) {
            fbOrder.cart = { ...fbOrder.wsData };
          }

          if (!Array.isArray(fbOrder.lines) || fbOrder.lines.length === 0) {
            console.warn("[handleVoid] fbOrder.lines is empty or missing, skipping:", fbOrder.fbOrderId);
            continue;
          }

          if (!Array.isArray(cancelItemIds) || cancelItemIds.length === 0) {
            console.warn("[handleVoid] cancelItemIds is empty or invalid, aborting cancellation");
            return;
          }

          const cancelRemarksData = useRestaurantStore.getState().cancelKOTRemarks || {};
          const globalRemarks = cancelRemarksData?.remarks || "";

          fbOrder.lines = fbOrder.lines.map((line) => {
            const isAlreadyFinal = line.isCanceled === "Y";
            const shouldCancel = !isAlreadyFinal && cancelItemIds.includes(line.fbOrderLineId);
            if (shouldCancel) {
              const remarks = lineRemarks?.[line.fbOrderLineId] || globalRemarks;
              return { ...line, isCanceled: "Y", remarks };
            }
            return line;
          });

          if (fbOrder.cart?.items?.length) {
            fbOrder.cart.items = fbOrder.cart.items.map((item) => {
              const isAlreadyFinal = ["RC", "CO", "CL"].includes(item.orderStatus);
              const shouldCancel = !isAlreadyFinal && cancelItemIds.includes(item.fbOrderLineId);
              return shouldCancel ? { ...item, isCanceled: "Y", orderStatus: "CL" } : item;
            });
          }

          // Recalculate summaries
          const saleTypeData = usePOSConfigStore.getState().saleTypeData || {};
          const taxIncludeFlag = saleTypeData?.customer?.istaxincluded === "Y" ? "Y" : tillData.tillAccess.csBunit.isTaxIncluded ? tillData.tillAccess.csBunit.isTaxIncluded : "Y";
          const itemsSummery = {
            decimalItems: { totalQty: 0, totalAmount: 0 },
            nonDecimalItems: { totalQty: 0, totalAmount: 0 },
            itemsQty: 0,
            items: 0,
          };

          const isInterState = getIsInterState(fbOrder.cart);
          const groupedItemsByTaxRate = groupItemsByTaxRate(fbOrder.cart?.items || [], precision, { isInterState, filterCanceled: true });

          let updatedTotalTax = 0;
          let updatedTotalPrice = 0;
          let updatedTotalItemsQty = 0;
          let updatedTotalDiscounts = 0;
          let updatedTotalStd = 0;

          if (fbOrder.cart?.items?.length) {
            fbOrder.cart.items = fbOrder.cart.items.map((item, i) => {
              if (item.isCanceled === "Y") {
                item.discount = 0;
                item.key = i;
                return item;
              }
              const nettotalFixed = parseFloat(parseFloat(item?.nettotal)?.toFixed(precision));
              const taxAmountFixed = parseFloat(parseFloat(item?.taxAmount)?.toFixed(precision));
              const discountFixed = item.discount ? parseFloat(parseFloat(item?.discount)?.toFixed(precision)) : 0;

              updatedTotalPrice += nettotalFixed;
              updatedTotalItemsQty += item.weight;
              updatedTotalTax += taxAmountFixed;
              updatedTotalDiscounts += discountFixed;

              item.discount = discountFixed;
              item.key = i;

              if (!item.isGiftCard) {
                const unitPrice = isFinite(item.nettotal / item.weight) ? (taxIncludeFlag === "Y" ? item.nettotal / item.weight / (1 + item.taxRate / 100) : item.salePrice) : 0;
                const lineTax = item.listPrice * (1 + (item.taxRate || 0) / 100);
                const stdTax = item.sunitprice * (1 + (item.taxRate || 0) / 100);
                const netList =
                  taxIncludeFlag === "Y" ? item.listPrice - parseFloat(parseFloat(item.listPrice - item.listPrice / (1 + item.taxRate / 100))?.toFixed(precision)) : item.listPrice;
                const gross_list = taxIncludeFlag === "Y" ? item.listPrice : parseFloat(parseFloat(lineTax)?.toFixed(precision));
                const grossStd = taxIncludeFlag === "Y" ? item.sunitprice : parseFloat(parseFloat(stdTax)?.toFixed(precision));

                Object.assign(item, {
                  linetax: nettotalFixed ? taxAmountFixed : 0,
                  linenet: nettotalFixed ? parseFloat(parseFloat(item.nettotal - item.taxAmount)?.toFixed(precision)) : 0,
                  linegross: nettotalFixed || 0,
                  linestd: nettotalFixed ? parseFloat(parseFloat(grossStd * item.weight)?.toFixed(precision)) : 0,
                  netunit: nettotalFixed ? parseFloat(parseFloat(unitPrice)?.toFixed(precision)) : 0,
                  listprice: nettotalFixed ? item.listPrice : 0,
                  grossunit: nettotalFixed ? parseFloat(parseFloat(unitPrice)?.toFixed(precision)) * (item.isReturn ? -1 : 1) : 0,
                  grossstd: nettotalFixed ? grossStd : 0,
                  grosslist: nettotalFixed ? roundTo(gross_list, precision) : 0,
                  netList: nettotalFixed ? netList : 0,
                  unitPrice: nettotalFixed ? parseFloat(parseFloat(unitPrice)?.toFixed(precision)) : 0,
                  taxAmount: nettotalFixed ? taxAmountFixed : 0,
                });

                updatedTotalStd += nettotalFixed ? parseFloat(parseFloat(grossStd * item.weight)?.toFixed(precision)) : 0;
              }

              if (Array.isArray(item.children) && item.children.length > 0) {
                item.children.forEach((child) => {
                  const isDecimal = child.uomData?.[0]?.decimal === "Y" || child.isDecimal;
                  const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";
                  itemsSummery.itemsQty += child.weight;
                  itemsSummery.items += 1;
                  itemsSummery[summaryKey].totalQty += child.weight;
                  itemsSummery[summaryKey].totalAmount += parseFloat(parseFloat(child.nettotal)?.toFixed(precision));
                });
              } else {
                const isDecimal = item.uomData?.[0]?.decimal === "Y" || item.isDecimal;
                const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";
                itemsSummery.itemsQty += item.weight;
                itemsSummery.items += 1;
                itemsSummery[summaryKey].totalQty += item.weight;
                itemsSummery[summaryKey].totalAmount += parseFloat(parseFloat(item.nettotal)?.toFixed(precision));
              }

              return item;
            });

            fbOrder.cart.total = parseFloat(updatedTotalPrice.toFixed(precision));
            fbOrder.cart.tax = updatedTotalTax;
            fbOrder.cart.discount = updatedTotalDiscounts;
            fbOrder.cart.totalQty = updatedTotalItemsQty;
            fbOrder.cart.subTotal = parseFloat(parseFloat(updatedTotalPrice - updatedTotalTax)?.toFixed(precision));
            fbOrder.cart.totalStd = updatedTotalStd;
            fbOrder.cart.itemsSummery = itemsSummery;
            fbOrder.cart.groupedItemsByTaxRate = groupedItemsByTaxRate;
          }

          if (fbOrder.wsData && typeof fbOrder.wsData === "object" && Object.keys(fbOrder.wsData).length > 0) {
            if (Array.isArray(fbOrder.wsData.items) && fbOrder.wsData.items.length > 0) {
              fbOrder.wsData = {
                ...fbOrder.wsData,
                items: fbOrder.wsData.items.map((item) => {
                  const isAlreadyFinal = item.isCanceled === "Y" || ["RC", "CO", "CL"].includes(item.orderStatus);
                  const shouldCancel = !isAlreadyFinal && cancelItemIds.includes(item.fbOrderLineId);
                  return shouldCancel ? { ...item, isCanceled: "Y", orderStatus: "CL" } : item;
                }),
                total: fbOrder.cart?.total,
                tax: fbOrder.cart?.tax,
                discount: fbOrder.cart?.discount,
                totalQty: fbOrder.cart?.totalQty,
                subTotal: fbOrder.cart?.subTotal,
                totalStd: fbOrder.cart?.totalStd,
                itemsSummery: fbOrder.cart?.itemsSummery,
                groupedItemsByTaxRate: fbOrder.cart?.groupedItemsByTaxRate,
              };
            }
          }

          if (fbOrder.cart) {
            setCart(fbOrder.cart);
          }

          const allLinesCancelled = fbOrder.lines.every((l) => l.isCanceled === "Y");
          const allCartItemsCancelled = fbOrder.cart?.items?.length ? fbOrder.cart.items.every((item) => item.isCanceled === "Y" || item.orderStatus === "CL") : true;
          const allCancelled = allLinesCancelled && allCartItemsCancelled;

          if (allCancelled) shouldClearCart = true;

          fbOrder.fbOrderStatus = allCancelled ? "CL" : "IP";
          fbOrder.isCanceled = allCancelled ? "Y" : "N";
          fbOrder.fbOrderSync = "N";

          await db.fbOrderData
            .where("fbOrderId")
            .equals(fbOrder.fbOrderId)
            .modify({
              fbOrderSync: "N",
              fbOrderStatus: fbOrder.fbOrderStatus,
              lines: fbOrder.lines,
              isCanceled: fbOrder.isCanceled,
              cart: fbOrder.cart,
              wsData: allCancelled ? {} : fbOrder.wsData,
            });

          if (fbOrder.cart) {
            setCart(fbOrder.cart);
          }

          await SyncData(fbOrder, "upsertFbOrder", null, "", true, fbOrder.cart);

          const response = await db.tableData.where("cwrFbTableId").equals(record?.cwrFbTableId).toArray();
          if (!response.length) continue;

          let tableData = { ...response[0] };

          if (tableData.isSplitParent === "Y" && tableData.splitTables) {
            tableData.splitTables = tableData.splitTables.map((split) => {
              if (split.splitTable !== fbOrder.splitTable) return split;
              return {
                ...split,
                status: allCancelled ? "OPN" : "ONG",
                cart: fbOrder.cart,
              };
            });

            const currentSplit = tableData.splitTables.find((s) => s.splitTable === fbOrder.splitTable);
            if (currentSplit?.cart?.items?.length && currentSplit.cart.items.every((i) => i.isCanceled === "Y")) {
              shouldClearCart = true;
            }

            tableData.tabbleSync = "N";
            await db.tableData.put(tableData, tableData.cwrFbTableId);

            await SyncData(
              {
                fbtableStatusId: tableData.fbtableStatusId,
                cwrFbTableId: tableData.cwrFbTableId,
                splitTables: tableData.splitTables,
                statusType: tableData.statusType,
                waiter: tableData.waiter,
              },
              "upsertTableStatus"
            );
          } else {
            tableData.statusType = allCancelled ? "OPN" : "ONG";
            tableData.color = "rgba(146, 144, 152, 0.5)";
            tableData.wsData = allCancelled ? {} : fbOrder.wsData;
            tableData.items = allCancelled ? [] : fbOrder.wsData?.items || [];
          }

          await SyncData(tableData, "upsertTableStatus");

          await db.tableData.update(tableData.cwrFbTableId, {
            color: "rgba(146, 144, 152, 0.5)",
            noOfPersons: allCancelled ? "" : tableData.noOfPersons,
            guestName: allCancelled ? "" : tableData.guestName,
            guestType: allCancelled ? "" : tableData.guestType,
            referredBy: allCancelled ? "" : tableData.referredBy,
            statusType: allCancelled ? "OPN" : "ONG",
            items: allCancelled ? [] : fbOrder.wsData?.items || [],
            wsData: allCancelled ? {} : fbOrder.wsData,
            ordered: allCancelled ? "N" : "Y",
          });

          const updatedTableData = await db.tableData.toArray();
          if (setTableCards) setTableCards(updatedTableData);
          if (setOcupiedTablesData) setOcupiedTablesData(updatedTableData.filter((t) => t.statusType === "OCU" || t.statusType === "ONG"));
          if (setReservedTablesData) setReservedTablesData(updatedTableData.filter((t) => t.statusType === "RES"));
          if (setTableList) setTableList(updatedTableData);

          history.push("/pos");
          setKeyValue("0");
          setShowProducts(false);
        }

        if (shouldClearCart) {
          clearCart1(cart.type);
        }
      } else {
        setCategorieFlag(false);
        clearCart1(cart.type);
      }
    }
  };

  return { handleVoid, cancelling };
};

export default useVoidHandler;
